import { useEffect, useMemo, useRef, useState } from "react";
import {
  analyzeCapabilities,
  analyzeDump,
  analyzeExits,
  fetchFixture,
  fetchFixtures,
  fetchProviderStatus,
  ApiError,
  type CapabilityResponse,
  type DumpResponse,
  type ExitResponse,
} from "./api";
import { FixtureGallery } from "./components/FixtureGallery";
import { LiveAnalysisForm } from "./components/LiveAnalysisForm";
import { ProviderPanel } from "./components/ProviderPanel";
import { ReportView } from "./components/ReportView";
import type { AnalysisBundle, FixtureSummary, ProviderStatus, TokenIdentity } from "./shared/report";

type SectionState = "idle" | "loading" | "ready" | "error";

interface LiveInput {
  address: string;
  usdAmounts?: number[];
  rawTokenAmounts?: string[];
}

export default function App() {
  const [fixtures, setFixtures] = useState<FixtureSummary[]>([]);
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  const [fixtureReport, setFixtureReport] = useState<AnalysisBundle | null>(null);
  const [fixtureBusy, setFixtureBusy] = useState(false);

  const [liveInput, setLiveInput] = useState<LiveInput | null>(null);
  const [capability, setCapability] = useState<CapabilityResponse | null>(null);
  const [dump, setDump] = useState<DumpResponse | null>(null);
  const [exits, setExits] = useState<ExitResponse | null>(null);
  const [capabilityState, setCapabilityState] = useState<SectionState>("idle");
  const [dumpState, setDumpState] = useState<SectionState>("idle");
  const [exitState, setExitState] = useState<SectionState>("idle");
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [dumpError, setDumpError] = useState<string | null>(null);
  const [exitError, setExitError] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<"fixture" | "live" | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const reportRef = useRef<HTMLHeadingElement>(null);
  const runId = useRef(0);

  const refreshProviders = async () => {
    try {
      setProviderStatus(await fetchProviderStatus());
    } catch {
      setStartupError("We couldn’t refresh the live data connections. Try again.");
    }
  };

  useEffect(() => {
    Promise.all([fetchFixtures(), fetchProviderStatus()])
      .then(([fixtureList, status]) => {
        setFixtures(fixtureList);
        setProviderStatus(status);
      })
      .catch(() => {
        setStartupError("Rug Lens couldn’t reach its analysis service. Try again.");
      });
  }, []);

  const chooseFixture = async (fixture: FixtureSummary) => {
    const currentRun = ++runId.current;
    setActiveMode("fixture");
    setSelectedFixtureId(fixture.id);
    setFixtureBusy(true);
    setFixtureReport(null);
    setAnnouncement(`Loading the ${fixture.title} made-up demo.`);
    clearLiveState();
    try {
      const result = await fetchFixture(fixture.id);
      if (runId.current !== currentRun) return;
      setFixtureReport(result);
      setAnnouncement(`Made-up demo report ready: ${result.fixtureLabel ?? fixture.title}.`);
    } catch {
      if (runId.current !== currentRun) return;
      setStartupError("We couldn’t load that demo. Try again.");
      setAnnouncement(`The ${fixture.title} made-up demo could not be loaded.`);
    } finally {
      if (runId.current === currentRun) setFixtureBusy(false);
    }
  };

  const runLive = (input: LiveInput) => {
    const currentRun = ++runId.current;
    setActiveMode("live");
    setSelectedFixtureId(null);
    setFixtureReport(null);
    setLiveInput(input);
    setCapability(null);
    setDump(null);
    setExits(null);
    setCapabilityError(null);
    setDumpError(null);
    setExitError(null);
    setCapabilityState("loading");
    setDumpState("loading");
    setExitState("loading");
    setAnnouncement("Live analysis started. Checking identity, rug capacity, dump capacity, and exit liquidity.");

    analyzeCapabilities(input.address)
      .then((result) => {
        if (runId.current !== currentRun) return;
        setCapability(result);
        setCapabilityState("ready");
      })
      .catch((error) => {
        if (runId.current !== currentRun) return;
        setCapabilityError(analysisError(error, "We couldn’t verify identity or controller powers."));
        setCapabilityState("error");
      });

    analyzeDump(input.address)
      .then((result) => {
        if (runId.current !== currentRun) return;
        setDump(result);
        setDumpState("ready");
      })
      .catch((error) => {
        if (runId.current !== currentRun) return;
        setDumpError(analysisError(error, "We couldn’t verify holder concentration."));
        setDumpState("error");
      });

    analyzeExits(input.address, {
      usdAmounts: input.usdAmounts,
      rawTokenAmounts: input.rawTokenAmounts,
    })
      .then((result) => {
        if (runId.current !== currentRun) return;
        setExits(result);
        setExitState("ready");
      })
      .catch((error) => {
        if (runId.current !== currentRun) return;
        setExitError(analysisError(error, "We couldn’t verify exit liquidity."));
        setExitState("error");
      });
  };

  const liveIdentity = mergeIdentity(capability?.identity ?? null, exits?.identity ?? null);
  const liveComplete = useMemo<AnalysisBundle | null>(() => {
    if (!liveInput || !capability || !dump || !exits) return null;
    return {
      mode: "live",
      fixtureId: null,
      fixtureLabel: null,
      identity: mergeIdentity(capability.identity, exits.identity)!,
      identityReport: capability.identityReport,
      rugCapacity: capability.rugCapacity,
      dumpCapacity: dump.dumpCapacity,
      executableExit: exits.executableExit,
      generatedAt: new Date().toISOString(),
    };
  }, [liveInput, capability, dump, exits]);

  const activeIdentity = activeMode === "fixture" ? fixtureReport?.identity ?? null : liveIdentity;
  const isLiveBusy = capabilityState === "loading" || dumpState === "loading" || exitState === "loading";
  const anyReportVisible = Boolean(activeIdentity);
  const totalLiveFailure =
    activeMode === "live" && capabilityState === "error" && dumpState === "error" && exitState === "error";
  const hasAnalysisResult = anyReportVisible || totalLiveFailure;

  useEffect(() => {
    if (activeMode !== "live" || !liveInput) return;
    const states = [capabilityState, dumpState, exitState];
    if (states.every((state) => state === "loading")) return;
    setAnnouncement(
      `Live analysis update. Identity and rug capacity ${announcedState(capabilityState)}. ` +
      `Dump capacity ${announcedState(dumpState)}. Exit liquidity ${announcedState(exitState)}.`,
    );
  }, [activeMode, liveInput, capabilityState, dumpState, exitState]);

  useEffect(() => {
    if (!hasAnalysisResult) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const focusTimer = window.setTimeout(() => {
      reportRef.current?.focus({ preventScroll: true });
      reportRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }, 40);
    return () => window.clearTimeout(focusTimer);
  }, [hasAnalysisResult]);

  const clearLiveState = () => {
    setLiveInput(null);
    setCapability(null);
    setDump(null);
    setExits(null);
    setCapabilityState("idle");
    setDumpState("idle");
    setExitState("idle");
    setCapabilityError(null);
    setDumpError(null);
    setExitError(null);
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Rug Lens home">
          <span className="brand-lens" aria-hidden="true"><span /></span>
          <span>
            <strong>RUG LENS</strong>
            <small>coin detective for besties</small>
          </span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#try-it">Try it</a>
          <a href="#how-it-reads">How it reads</a>
          <a href="#about">Limits</a>
        </nav>
        <span className="header-status"><span /> Solana mainnet</span>
      </header>

      <main id="main-content" tabIndex={-1}>
        <section className="hero" id="top">
          <div className="hero-orbit hero-orbit-one" aria-hidden="true" />
          <div className="hero-orbit hero-orbit-two" aria-hidden="true" />
          <span className="hero-sticker hero-sticker-one" aria-hidden="true">tiny coin detective ✦</span>
          <span className="hero-sticker hero-sticker-two" aria-hidden="true">receipts &gt; vibes</span>
          <div className="hero-copy">
            <div className="chain-row" role="group" aria-label="Supported chains">
              <span className="chain-chip chain-live"><span /> Solana live</span>
            </div>
            <p className="hero-kicker">Cute coin? Check the teeth.</p>
            <h1>
              Before you buy.
              <span>Peek under the rug.</span>
            </h1>
            <p className="hero-lede">
              Paste the Solana mint address. We check whether registries recognize it, who can change
              it, who could dump it, and whether your $100, $1,000, or $10,000 exit works right
              now. Missing receipts stay <em>Unknown</em>—we never paint a guess green.
            </p>
            <div className="hero-actions" role="group" aria-label="Choose how to try Rug Lens">
              <div className="hero-action-choice">
                <a
                  className="button button-primary"
                  href="#live-analysis"
                  aria-describedby="hero-live-helper"
                >
                  Check a live token
                </a>
                <small id="hero-live-helper">Paste a Solana mint address</small>
              </div>
              <div className="hero-action-choice">
                <a
                  className="button button-secondary"
                  href="#guided-examples-title"
                  aria-describedby="hero-demo-helper"
                >
                  Try a made-up demo
                </a>
                <small id="hero-demo-helper">Show me the drama—no address needed</small>
              </div>
            </div>
            <ul className="promise-list" aria-label="Product promises">
              <li>No wallet nonsense</li>
              <li>No vibes-only score</li>
              <li>No signing anything</li>
            </ul>
          </div>

          <div className="hero-demo" role="group" aria-label="Example Rug Lens report card">
            <div className="demo-window-bar"><span /><span /><span /><small>TINY DRAMA REPORT</small></div>
            <div className="demo-token-row">
              <div className="demo-token-icon">?</div>
              <div><small>UNTRUSTED METADATA</small><strong>Example Token <span>EXMPL</span></strong></div>
              <span className="demo-live-pill">made-up demo</span>
            </div>
            <div className="demo-question">
              <small>THE GOSSIP, WITH RECEIPTS</small>
              <strong>What could ruin your day</strong>
            </div>
            <div className="demo-capabilities">
              <div className="demo-capability is-unknown"><span>?</span><div><small>Identity</small><strong>Unverified</strong></div></div>
              <div className="demo-capability is-risk"><span>!</span><div><small>Rug capacity</small><strong>2 powers active</strong></div></div>
              <div className="demo-capability is-risk"><span>!</span><div><small>Dump capacity</small><strong>31% wallet-shaped</strong></div></div>
              <div className="demo-capability is-risk"><span>!</span><div><small>Exit liquidity</small><strong>$10k returns 34.2%</strong></div></div>
            </div>
            <div className="demo-exit-head"><small>SELL QUOTES BY SIZE</small><span>A quote isn’t a guaranteed sale</span></div>
            <div className="demo-exit-row"><span>$100</span><div><i style={{ width: "98%" }} /></div><strong>98.4%</strong></div>
            <div className="demo-exit-row"><span>$1k</span><div><i style={{ width: "87%" }} /></div><strong>87.0%</strong></div>
            <div className="demo-exit-row"><span>$10k</span><div><i style={{ width: "34%" }} /></div><strong>34.2%</strong></div>
            <p className="demo-footnote">Made-up example · open any claim to see its source, timing, and coverage</p>
          </div>
        </section>

        <section className="three-promises" id="how-it-reads" aria-labelledby="four-checks-title">
          <div className="section-heading wide-heading">
            <div>
              <p className="eyebrow">Four questions. Zero crystal balls.</p>
              <h2 id="four-checks-title">Before the group chat says “send it.”</h2>
            </div>
            <p>Get one plain answer first. The nerdy receipts stay behind “Show full evidence” until you want them.</p>
          </div>
          <div className="promise-cards">
            <article><span>01</span><h3>Identity</h3><p>Do Jupiter or CoinGecko recognize the complete mint? Each source is named; copied branding, age, and popularity do not prove identity.</p></article>
            <article><span>02</span><h3>Rug capacity</h3><p>Can someone mint, freeze, seize, tax, block, pull liquidity, or rewrite how the token behaves?</p></article>
            <article><span>03</span><h3>Dump capacity</h3><p>Could one wallet-shaped holder overwhelm buyers? Program vaults are separated instead of being mislabeled as insiders.</p></article>
            <article><span>04</span><h3>Exit liquidity</h3><p>What do $100, $1,000, and $10,000 exits return now? A working small exit proves nothing about a larger one.</p></article>
          </div>
          <p className="unknown-rule"><strong>One non-negotiable:</strong> unsupported code, missing context, and data-source failures stay Unknown. They never get painted green.</p>
        </section>

        <section className="try-section" id="try-it" aria-labelledby="try-title">
          <div className="try-heading">
            <div>
              <p className="eyebrow">Pick your plot twist</p>
              <h2 id="try-title">Try a tiny drama, or check a live Solana token</h2>
            </div>
            <p>Made-up examples teach the result in seconds. Live checks need only a mint address—never a wallet connection or signature.</p>
          </div>

          {startupError && (
            <div className="startup-error" role="alert">
              <strong>Rug Lens is having trouble loading.</strong>
              <p>{startupError}</p>
              <button className="button button-secondary" type="button" onClick={() => window.location.reload()}>Retry</button>
            </div>
          )}

          <ProviderPanel status={providerStatus} onRefresh={refreshProviders} />
          <FixtureGallery
            fixtures={fixtures}
            selectedId={selectedFixtureId}
            busy={fixtureBusy || isLiveBusy}
            onSelect={chooseFixture}
          />
          <div className="path-divider"><span>or check a live Solana token</span></div>
          <LiveAnalysisForm busy={isLiveBusy || fixtureBusy} onAnalyze={runLive} />
        </section>

        <section
          className="report-anchor"
          aria-labelledby={hasAnalysisResult ? "analysis-result-title" : undefined}
          aria-busy={activeMode === "live" && isLiveBusy}
        >
          {hasAnalysisResult && (
            <h2 className="sr-only report-focus-target" id="analysis-result-title" ref={reportRef} tabIndex={-1}>
              Analysis result
            </h2>
          )}
          {activeMode === "fixture" && fixtureReport && (
            <ReportView
              mode="fixture"
              fixtureLabel={fixtureReport.fixtureLabel}
              identity={fixtureReport.identity}
              identityReport={fixtureReport.identityReport}
              rugCapacity={fixtureReport.rugCapacity}
              dumpCapacity={fixtureReport.dumpCapacity}
              executableExit={fixtureReport.executableExit}
              identityState="ready"
              capabilityState="ready"
              dumpState="ready"
              exitState="ready"
              completeReport={fixtureReport}
            />
          )}

          {activeMode === "live" && activeIdentity && (
            <ReportView
              mode="live"
              identity={activeIdentity}
              identityReport={capability?.identityReport ?? null}
              rugCapacity={capability?.rugCapacity ?? null}
              dumpCapacity={dump?.dumpCapacity ?? null}
              executableExit={exits?.executableExit ?? null}
              identityState={capabilityState}
              capabilityState={capabilityState}
              dumpState={dumpState}
              exitState={exitState}
              capabilityError={capabilityError}
              dumpError={dumpError}
              exitError={exitError}
              completeReport={liveComplete}
              onRefreshLive={() => liveInput && runLive(liveInput)}
            />
          )}

          {activeMode === "live" && !activeIdentity && (isLiveBusy || totalLiveFailure) && (
            <div className="analysis-start-card">
              <div className="loading-lens" aria-hidden="true"><span /></div>
              <div>
                <p className="eyebrow">Live Solana analysis</p>
                <h2>{totalLiveFailure ? "We couldn’t verify these checks right now" : "Checking four things…"}</h2>
                <div className="analysis-paths">
                  <AnalysisPath label="Exact token identity" state={capabilityState} message={capabilityError} />
                  <AnalysisPath label="Controller powers" state={capabilityState} message={capabilityError} />
                  <AnalysisPath label="Holder concentration" state={dumpState} message={dumpError} />
                  <AnalysisPath label="Exit liquidity by size" state={exitState} message={exitError} />
                </div>
                {totalLiveFailure && <button className="button button-secondary" type="button" onClick={() => liveInput && runLive(liveInput)}>Try again</button>}
              </div>
            </div>
          )}
        </section>

        <section className="about-section" id="about">
          <div className="about-quote">
            <span aria-hidden="true">“</span>
            <blockquote>
              Cute logo. Loud timeline. Still check the damn address.
            </blockquote>
          </div>
          <div className="about-columns">
            <div><p className="eyebrow">Identity</p><h2>Is it the right mint?</h2><p>Checks the full address against Jupiter verification and CoinGecko’s Solana contract listing. Every positive source is named; age and popularity stay context—never proof.</p></div>
            <div><p className="eyebrow">Rug Capacity</p><h2>What controllers can do</h2><p>Checks for powers that can dilute supply, freeze or seize holdings, block transfers, change fees, pull liquidity, or rewrite behavior.</p></div>
            <div><p className="eyebrow">Dump Capacity</p><h2>Who holds sellable supply</h2><p>Groups the largest token accounts by owner and separates wallet-shaped, multisig, program-controlled, and unresolved holdings without pretending to know intent.</p></div>
            <div><p className="eyebrow">Exit Liquidity</p><h2>What each sell size returns</h2><p>Tests distinct sell sizes, shows expected proceeds, and says clearly whether the evidence is a quote or a stronger simulation.</p></div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="brand footer-brand"><span className="brand-lens" aria-hidden="true"><span /></span><span><strong>RUG LENS</strong><small>receipts over vibes</small></span></div>
        <p>Read the receipts. Keep your wallet out of it. No signing, no opaque verdict, no financial advice.</p>
        <a href="#top">Back to top ↑</a>
      </footer>
    </div>
  );
}

function analysisError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  if (["BAD_INPUT", "NOT_FOUND", "UNSUPPORTED_TOKEN", "NO_ROUTE", "UNEXPECTED_NETWORK"].includes(error.code)) {
    return error.message;
  }
  if (error.code === "PROVIDER_RATE_LIMITED") {
    return "A data source is busy right now. Try again in a moment.";
  }
  if (["NETWORK", "BAD_RESPONSE", "PROVIDER_AUTH", "PROVIDER_UNAVAILABLE"].includes(error.code)) {
    return "A data source couldn’t complete this check. Try again shortly.";
  }
  return fallback;
}

function AnalysisPath({ label, state, message }: { label: string; state: SectionState; message: string | null }) {
  return (
    <div className={`analysis-path analysis-path-${state}`}>
      <span aria-hidden="true">{state === "ready" ? "✓" : state === "error" ? "?" : "·"}</span>
      <div><strong>{label}</strong><small>{state === "loading" ? "Reading…" : state === "error" ? message : state}</small></div>
    </div>
  );
}

function announcedState(state: SectionState): string {
  if (state === "ready") return "ready";
  if (state === "error") return "could not be verified";
  if (state === "loading") return "still checking";
  return "waiting";
}

function mergeIdentity(
  primary: TokenIdentity | null,
  secondary: TokenIdentity | null,
): TokenIdentity | null {
  if (!primary) return secondary;
  if (!secondary) return primary;
  return {
    ...primary,
    name: primary.name ?? secondary.name,
    symbol: primary.symbol ?? secondary.symbol,
  };
}
